import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { MapPin, Sun, Users, ShieldCheck } from "lucide-react";
import { AURORA_ANCHORS, AURORA_THEME } from "./theme";

// The Aurora Promise — the campaign's core message. Four promise cards
// (right place / right time / worthwhile wait / 50% credits refund) with the
// refund promise visually elevated, plus the campaign-window eligibility note
// that anchor-scrolls into the FAQ for full terms.

const PROMISES = [
  { key: "right_place", Icon: MapPin },
  { key: "right_time", Icon: Sun },
  { key: "wait_worthwhile", Icon: Users },
  { key: "stand_behind", Icon: ShieldCheck, highlighted: true },
];

const PromiseCard = ({ promiseKey, Icon, highlighted, index }) => {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: 0.08 * index, duration: 0.5 }}
      className="tw-relative tw-flex tw-flex-col tw-overflow-hidden tw-rounded-3xl tw-p-7 md:tw-p-8"
      style={{
        background: AURORA_THEME.gradients.cardSurface,
        border: highlighted
          ? `1px solid ${AURORA_THEME.accent.primary}`
          : "1px solid rgba(0, 255, 204, 0.18)",
        boxShadow: highlighted ? AURORA_THEME.glow.grandPrize : AURORA_THEME.glow.soft,
      }}
    >
      <div className="tw-mb-4 tw-flex tw-items-center tw-gap-3">
        <div
          className="tw-flex tw-h-12 tw-w-12 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full"
          style={{
            backgroundColor: AURORA_THEME.accent.primarySoft,
            color: AURORA_THEME.accent.primary,
          }}
        >
          <Icon className="tw-h-6 tw-w-6" />
        </div>
        <span
          className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest"
          style={{ color: AURORA_THEME.accent.primary }}
        >
          {t("campaign.aurora.promises.label", { number: index + 1 })}
        </span>
      </div>

      <h3 className="tw-text-xl tw-font-bold tw-text-white md:tw-text-2xl">
        {t(`campaign.aurora.promises.${promiseKey}.title`)}
      </h3>
      <p
        className="tw-mt-3 tw-text-sm tw-leading-relaxed md:tw-text-base"
        style={{ color: AURORA_THEME.text.secondary }}
        dangerouslySetInnerHTML={{
          __html: t(`campaign.aurora.promises.${promiseKey}.description`),
        }}
      />
    </motion.div>
  );
};

const AuroraPromises = () => {
  const { t } = useTranslation();

  const scrollToFaqs = (e) => {
    e.preventDefault();
    const target = document.getElementById(AURORA_ANCHORS.faqs);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section id={AURORA_ANCHORS.promises} className="tw-relative tw-w-full tw-py-12 md:tw-py-16">
      <div className="tw-mx-auto tw-max-w-6xl tw-px-4 sm:tw-px-6 lg:tw-px-8">
        <div className="tw-mb-10 tw-text-center md:tw-mb-12">
          <h2 className="tw-text-2xl tw-font-extrabold tw-text-white md:tw-text-4xl">
            {t("campaign.aurora.promises.heading")}
          </h2>
          <p
            className="tw-mx-auto tw-mt-3 tw-max-w-2xl tw-text-sm md:tw-text-base lg:tw-text-lg"
            style={{ color: AURORA_THEME.text.secondary }}
          >
            {t("campaign.aurora.promises.subheading")}
          </p>
        </div>

        <div className="tw-grid tw-grid-cols-1 tw-gap-5 md:tw-grid-cols-2 md:tw-gap-6">
          {PROMISES.map(({ key, Icon, highlighted }, index) => (
            <PromiseCard
              key={key}
              promiseKey={key}
              Icon={Icon}
              highlighted={highlighted}
              index={index}
            />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5 }}
          className="tw-mt-8 tw-flex tw-flex-col tw-items-center tw-gap-1.5 tw-rounded-2xl tw-px-5 tw-py-4 tw-text-center"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.25)",
            border: "1px solid rgba(0, 255, 204, 0.15)",
          }}
        >
          <p className="tw-text-sm tw-font-semibold md:tw-text-base" style={{ color: AURORA_THEME.accent.warm }}>
            {t("campaign.aurora.promises.campaign_note")}
          </p>
          <a
            href={`#${AURORA_ANCHORS.faqs}`}
            onClick={scrollToFaqs}
            className="tw-text-xs tw-font-medium tw-underline md:tw-text-sm"
            style={{ color: AURORA_THEME.text.secondary }}
          >
            {t("campaign.aurora.promises.terms_link")}
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default AuroraPromises;
